import { useMemo, useState } from 'react';
import { Truck } from 'lucide-react';
import { formatAgo } from '@/lib/format';
import { useImportVehicles, usePollNow, type ImportReport, type LinkReport } from '@/api/hooks/integrations';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Input } from '@/components/ui/Field';
import { toast } from '@/stores/toast';

/** Най-малкият свободен номер в автопарка: 01, 02, … — както са номерирани бусовете досега. */
function suggestNumbers(count: number, used: Set<string>): string[] {
  const out: string[] = [];
  for (let n = 1; out.length < count && n < 1000; n++) {
    const candidate = String(n).padStart(2, '0');
    if (!used.has(candidate)) out.push(candidate);
  }
  return out;
}

/**
 * ВНАСЯНЕ НА БУСОВЕТЕ ОТ FROTCOM. Всеки бус във Frotcom без двойник при нас
 * може да стане наш бус — с регистрационния номер, марката и модела оттам и
 * вече свързан с GPS. Номерът в автопарка се предлага, но се пише тук.
 * Нашите бусове, които ги няма във Frotcom (примерните), могат да се махнат
 * наведнъж: курсовете с тях остават в историята, номерата им се освобождават.
 */
export function ImportVehiclesDialog({
  providerId,
  report,
  onClose,
  onDone,
}: {
  providerId: string;
  report: LinkReport;
  onClose: () => void;
  onDone: (result: ImportReport) => void;
}) {
  const importVehicles = useImportVehicles(providerId);
  const poll = usePollNow();
  const theirs = useMemo(
    () => [...report.unmatchedTheirs].sort((a, b) => (a.plate ?? a.name).localeCompare(b.plate ?? b.name, 'bg')),
    [report.unmatchedTheirs],
  );
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(theirs.filter((v) => v.plate).map((v) => v.externalId)));
  const [remove, setRemove] = useState<Set<string>>(() => new Set(report.unmatchedOurs.map((v) => v.vehicleId)));
  const [typed, setTyped] = useState<Record<string, string>>({});

  /** Заети остават номерата на свързаните бусове и на онези, които не се махат. */
  const used = useMemo(
    () =>
      new Set(
        [...report.linked.map((v) => v.fleetNo), ...report.unmatchedOurs.filter((v) => !remove.has(v.vehicleId)).map((v) => v.fleetNo)].map((n) =>
          n.trim().toUpperCase(),
        ),
      ),
    [report, remove],
  );
  const picked = theirs.filter((v) => chosen.has(v.externalId));
  const suggestions = suggestNumbers(picked.length, new Set([...used, ...Object.values(typed).map((n) => n.trim().toUpperCase())]));
  let next = 0;
  const numbers: Record<string, string> = {};
  for (const v of picked) numbers[v.externalId] = typed[v.externalId] ?? suggestions[next++] ?? '';

  const values = picked.map((v) => numbers[v.externalId]!.trim().toUpperCase());
  const duplicate = values.find((n, i) => n && values.indexOf(n) !== i);
  const clash = values.find((n) => used.has(n));
  const empty = values.some((n) => !n);
  const problem = empty ? 'Всеки бус трябва да има номер в автопарка.' : duplicate ? `Номер ${duplicate} е два пъти.` : clash ? `Номер ${clash} вече е зает от бус, който остава.` : null;

  const toggle = (set: Set<string>, key: string) => {
    const copy = new Set(set);
    if (copy.has(key)) copy.delete(key);
    else copy.add(key);
    return copy;
  };

  const submit = () =>
    importVehicles.mutate(
      {
        vehicles: picked.map((v) => ({ externalId: v.externalId, fleetNo: numbers[v.externalId]!.trim() })),
        removeVehicleIds: [...remove],
      },
      {
        onSuccess: (result) => {
          toast.ok(
            'Бусовете са внесени',
            `${result.created.length} нови, ${result.linked.length} свързани, ${result.removed.length} махнати. Позициите идват до минута.`,
          );
          poll.mutate();
          onDone(result);
        },
        onError: (error) => toast.error('Бусовете не са внесени', error instanceof Error ? error.message : undefined),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width="xl"
      title="Внеси бусовете от Frotcom"
      description="Отметнатите стават бусове в автопарка — с номера, марката и модела от Frotcom и вече свързани с GPS."
      footer={
        <>
          {problem ? <span className="mr-auto self-center text-xs text-danger">{problem}</span> : null}
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button
            variant="primary"
            icon={<Truck className="size-4" />}
            loading={importVehicles.isPending}
            disabled={Boolean(problem) || (picked.length === 0 && remove.size === 0)}
            onClick={submit}
          >
            {`Внеси ${picked.length}${remove.size ? ` и махни ${remove.size}` : ''}`}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <section>
          <h3 className="mb-2 text-sm font-semibold">
            От Frotcom <span className="font-normal text-ink-dim">— {theirs.length} без двойник при нас</span>
          </h3>
          {theirs.length === 0 ? (
            <p className="text-sm text-ink-dim">Всички бусове от Frotcom вече са свързани.</p>
          ) : (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {theirs.map((v) => {
                const on = chosen.has(v.externalId);
                return (
                  <li key={v.externalId} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
                    <Checkbox
                      label={<span className="font-mono text-ink">{v.plate ?? v.name}</span>}
                      checked={on}
                      disabled={!v.plate}
                      onChange={() => setChosen((prev) => toggle(prev, v.externalId))}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-ink-dim">
                      {[v.make, v.model].filter(Boolean).join(' ') || '—'}
                      {v.lastSeenAt ? ` · сигнал ${formatAgo(v.lastSeenAt)}` : ''}
                      {!v.plate ? ' · няма регистрационен номер във Frotcom' : ''}
                    </span>
                    {on ? (
                      <label className="flex items-center gap-1.5 text-xs text-ink-dim">
                        № в автопарка
                        <Input
                          className="h-8 w-20 font-mono"
                          value={numbers[v.externalId] ?? ''}
                          onChange={(e) => setTyped((prev) => ({ ...prev, [v.externalId]: e.target.value }))}
                        />
                      </label>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {report.unmatchedOurs.length > 0 ? (
          <section>
            <h3 className="mb-1 text-sm font-semibold">
              При нас, но ги няма във Frotcom <span className="font-normal text-ink-dim">— {report.unmatchedOurs.length}</span>
            </h3>
            <p className="mb-2 text-xs text-ink-dim">
              Това са примерните бусове от пробата. Отметнатите се махат от списъците; курсовете и заявките с тях остават в
              историята, а номерата им се освобождават за истинските.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {report.unmatchedOurs.map((v) => (
                <Checkbox
                  key={v.vehicleId}
                  label={
                    <span>
                      <span className="font-display font-semibold text-ink">{v.fleetNo}</span>{' '}
                      <span className="font-mono">{v.plate}</span>
                    </span>
                  }
                  checked={remove.has(v.vehicleId)}
                  onChange={() => setRemove((prev) => toggle(prev, v.vehicleId))}
                />
              ))}
            </div>
          </section>
        ) : null}

        {report.linked.length > 0 ? (
          <section>
            <h3 className="mb-1 text-sm font-semibold">Вече свързани — остават, както са</h3>
            <div className="flex flex-wrap gap-1.5">
              {report.linked.map((v) => (
                <Badge key={v.vehicleId} tone="ok">
                  {v.fleetNo} · {v.plate}
                </Badge>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
